import { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from 'react-router-dom';
import FeatureIcons from '@/components/admin/common/FeatureIcons';
import SubscriptionProductTypeIcons from '@/components/admin/common/SubscriptionProductTypeIcons';
import {
  faSpinner,
  faSearch,
  faXmark,
  faUsers,
  faUserGroup,
  faStarfighterTwinIonEngine
} from '@/fontawesome';
import { handleAPIError } from '@/utils/toasts';
import { formatDate, formatRelativeTime, shortObjectID } from '@/utils/helpers';
import { APIRoutes } from '@/services/api.routes';
import { isEmptyFeatureConfig, type User, type CourseID } from '@/models';

interface CourseUsersTabProps {
  courseId: CourseID;
}

export default function CourseUsersTab({ courseId }: CourseUsersTabProps) {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await APIRoutes.listCourseUsers(courseId, {
        search: debouncedSearchTerm !== '' ? debouncedSearchTerm : undefined
      });
      setUsers(response.users);
    } catch (err) {
      console.error('Error loading course users:', err);
      setError('Fehler beim Laden der Benutzerliste');
      handleAPIError(err);
    } finally {
      setLoading(false);
    }
  }, [courseId, debouncedSearchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <div>
      <div className="mb-4">
        <div className="relative">
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
          />
          <input
            type="text"
            placeholder="Suche nach Name oder E-Mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          {searchTerm !== '' && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
            >
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {loading && (
        <div className="flex justify-center items-center py-12">
          <FontAwesomeIcon icon={faSpinner} className="w-8 h-8 text-blue-600 dark:text-blue-400 animate-spin" />
        </div>
      )}

      {error !== null && (
        <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg mb-6">
          <p className="text-red-600 dark:text-red-400">{error}</p>
        </div>
      )}

      {!loading && users.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Benutzer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Benutzer-ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Rolle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Features
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Subscriptions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Erstellt
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Zuletzt aktiv
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/admin/users/${user.id}`} className="block">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {user.firstName} {user.lastName}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {user.email}
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/admin/users/${user.id}`} className="block">
                        <div className="text-sm text-gray-500 dark:text-gray-400 font-mono">
                          {shortObjectID(user.id)}
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/admin/users/${user.id}`} className="block">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            user.permissionGroup === 'ADMIN'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-400'
                              : user.permissionGroup === 'EMPLOYEE'
                              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-400'
                              : 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-400'
                          }`}>
                            {user.permissionGroup === 'ADMIN' ? 'Administrator' : user.permissionGroup === 'EMPLOYEE' ? 'Mitarbeiter' : 'Benutzer'}
                          </span>
                          {user.featureConfig.communityMember && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-400" title="Community-Mitglied">
                              <FontAwesomeIcon icon={faUserGroup} className="w-3 h-3" />
                            </span>
                          )}
                          {!isEmptyFeatureConfig(user.manualFeatureConfig) && (
                            <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/50 dark:text-fuchsia-400" title="Feature-Config manuell überschrieben">
                              <FontAwesomeIcon icon={faStarfighterTwinIonEngine} className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/admin/users/${user.id}`} className="block">
                        <FeatureIcons featureConfig={user.featureConfig} />
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link to={`/admin/users/${user.id}`} className="block">
                        <SubscriptionProductTypeIcons productTypes={user.activeSubscriptionProductTypes} />
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <Link to={`/admin/users/${user.id}`} className="block">
                        {formatDate(user.created)}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      <Link to={`/admin/users/${user.id}`} className="block">
                        {formatRelativeTime(user.lastActive)}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && users.length === 0 && error === null && (
        <div className="text-center py-12">
          <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-gray-400">
            {debouncedSearchTerm !== '' ? 'Keine Benutzer gefunden, die der Suche entsprechen' : 'Keine Benutzer mit Zugriff auf diesen Kurs'}
          </p>
        </div>
      )}
    </div>
  );
}
